import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { staff } from "../domain/data";
import { useEvents } from "../state/events";
import { type CheckInResult } from "../domain/registrations";
import { useAuth } from "../state/AuthProvider";
import { useStudents } from "../state/students";
import { PrototypeBanner, StaffNav } from "../components/ui";

function flashClass(result: CheckInResult): string {
  if (result === "checked_in") return "flash flash--success";
  if (result === "already") return "flash flash--warning";
  return "flash flash--error flash--appear";
}

function resultMessage(
  result: CheckInResult,
  name: string,
): string {
  switch (result) {
    case "checked_in":
      return `Checked in ${name}`;
    case "already":
      return `${name} is already checked in`;
    case "not_registered":
      return `${name} is not registered for this event`;
    case "waitlisted":
      return `${name} is on the waitlist and does not have a seat yet`;
    case "cancelled":
      return `${name}'s registration was cancelled`;
    case "invalid":
      return "Could not check in that student";
    case "wrong_event":
      return "That registration is for a different event";
    case "event_closed":
      return "This event is archived. Check-in is closed.";
  }
}

export function StaffCheckIn() {
  const { eventId = "fall-activity-day" } = useParams();
  const { getEvent, registrationsFor, checkInStudent, undoCheckIn, countsFor } = useEvents();
  const { getStudent } = useStudents();
  const { account } = useAuth();
  const event = getEvent(eventId);
  const [picked, setPicked] = useState("");
  const [query, setQuery] = useState("");
  const [lastCheckedIn, setLastCheckedIn] = useState<{
    studentId: string;
    name: string;
  } | null>(null);
  const [result, setResult] = useState<{
    status: CheckInResult;
    message: string;
  } | null>(null);

  const staffDisplayName = account
    ? `${account.given} ${account.family}`
    : `${staff.given} ${staff.family}`;

  const rows = registrationsFor(eventId);
  const counts = countsFor(eventId);
  const waiting = useMemo(
    () =>
      rows.filter((row) => {
        const student = getStudent(row.studentId);
        if (!student || row.status !== "registered" || row.checkedInAt) {
          return false;
        }
        const q = query.trim().toLowerCase();
        if (!q) return true;
        return `${student.given} ${student.family}`.toLowerCase().includes(q);
      }),
    [query, rows, getStudent],
  );

  if (!event) {
    return (
      <div className="app">
        <PrototypeBanner />
        <main className="shell">
          <p>Event not found.</p>
          <Link to="/staff/events">Back to events</Link>
        </main>
      </div>
    );
  }

  function scanStudent(studentId: string) {
    const student = getStudent(studentId);
    const name = student ? `${student.given} ${student.family}` : "Student";
    const status = checkInStudent(eventId, studentId);
    setResult({ status, message: resultMessage(status, name) });
    if (status === "checked_in") {
      setLastCheckedIn({ studentId, name });
    } else {
      setLastCheckedIn(null);
    }
  }

  function onUndo() {
    if (!lastCheckedIn) return;
    undoCheckIn(eventId, lastCheckedIn.studentId);
    setResult({
      status: "invalid",
      message: `Undid check-in for ${lastCheckedIn.name}`,
    });
    setLastCheckedIn(null);
  }

  const nextWaiting = waiting[0];
  const nextStudent = nextWaiting ? getStudent(nextWaiting.studentId) : undefined;

  return (
    <div className="app">
      <PrototypeBanner />
      <main className="shell">
        <StaffNav />
        <p className="meta">
          Staff · {staffDisplayName} · check in
        </p>
        <h1>Check in</h1>
        <p className="lede">
          {event.name}. {counts.registered} registered · {counts.checkedIn}{" "}
          checked in · {counts.remaining} spots left
          {counts.waitlisted ? ` · ${counts.waitlisted} on the waitlist` : ""}.
        </p>

        <div className="wire">
          <p className="lede" style={{ marginTop: 0 }}>
            Door check-in for {staffDisplayName}. Tap a student name or use the list
            below.
          </p>
          <div className="actions" style={{ marginTop: 0 }}>
            <button
              type="button"
              disabled={!nextWaiting}
              onClick={() => nextWaiting && scanStudent(nextWaiting.studentId)}
            >
              Check in next waiting student
            </button>
            <Link className="btn ghost" to={`/staff/events/${event.id}`}>
              Attendance list
            </Link>
          </div>
          {nextStudent ? (
            <p className="anno">
              Next: {nextStudent.given} {nextStudent.family}
            </p>
          ) : (
            <p className="anno">No students waiting to check in.</p>
          )}
        </div>

        {result ? (
          <div
            key={result.message}
            className={flashClass(result.status)}
            role={result.status === "checked_in" ? "status" : "alert"}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "0.5rem",
            }}
          >
            <span>{result.message}</span>
            {result.status === "checked_in" && lastCheckedIn ? (
              <button
                type="button"
                className="btn ghost"
                style={{
                  padding: "0.2rem 0.6rem",
                  fontSize: "0.85rem",
                  background: "var(--bg)",
                }}
                onClick={onUndo}
              >
                Undo
              </button>
            ) : null}
          </div>
        ) : null}

        <div className="wire" style={{ marginTop: "1.25rem" }}>
          <div className="field">
            <label htmlFor="scan-pick">Check in this student</label>
            <select
              id="scan-pick"
              value={picked}
              onChange={(e) => setPicked(e.target.value)}
            >
              <option value="">Registered, not checked in</option>
              {waiting.map((row) => {
                const s = getStudent(row.studentId);
                if (!s) return null;
                return (
                  <option key={row.id} value={row.studentId}>
                    {s.given} {s.family}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="actions">
            <button
              type="button"
              disabled={!picked}
              onClick={() => {
                scanStudent(picked);
                setPicked("");
              }}
            >
              Check in
            </button>
          </div>
        </div>

        <h2>Waiting students</h2>
        <p className="lede">Tap a row to check in.</p>
        <div className="field">
          <label htmlFor="scan-search">Student name</label>
          <input
            id="scan-search"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Mike Smith"
          />
        </div>
        {waiting.length === 0 ? (
          <div className="wire dashed" style={{ marginTop: "0.75rem" }}>
            <div className="meta">
              {query
                ? "No matching students waiting"
                : "No students waiting to check in"}
            </div>
          </div>
        ) : (
          <div className="table-wrap" style={{ marginTop: "0.75rem" }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>School</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {waiting.map((row) => {
                  const s = getStudent(row.studentId);
                  if (!s) return null;
                  return (
                    <tr
                      key={row.id}
                      className="scan-row"
                      onClick={() => scanStudent(row.studentId)}
                    >
                      <td>
                        {s.given} {s.family}
                      </td>
                      <td>{s.school}</td>
                      <td>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            scanStudent(row.studentId);
                          }}
                        >
                          Check in
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
